import { Edit, PlayArrowOutlined, VerifiedOutlined } from '@mui/icons-material';
import { Button, Tooltip } from '@mui/material';
import React, { useCallback, useState } from 'react';

import type { ProjectRunAnalysisQuestion } from '../../../Types/dealGPT';
import { ProcessReason, UpdatedReason } from '../../../Types/enums';
import { AnalyticsEvent, useAnalytics } from '../../Providers/AnalyticsProvider';
import ApplyChangesDialog from './ApplyChangesDialog';
import RegenerateDialog from './RegenerateDialog';
interface SectionButtonsProps {
  projectId: string;
  handleSave: (
    values: Record<string, string>,
    reason?: UpdatedReason,
    processReason?: ProcessReason,
    overwriteQuestionData?: Partial<ProjectRunAnalysisQuestion>
  ) => Promise<void>;
  values: Record<string, string>;
  hasGenerated: boolean;
  showEditButton?: boolean;
  handleEdit?: (value: boolean) => void;
  isEditing?: boolean;
  showSaveButton?: boolean;
  allowGeneration?: boolean;
  allowReprompt?: boolean;
  question?: ProjectRunAnalysisQuestion;
  timeToProcess?: string;
  showGenerateButton?: boolean;
  isMountedOnMenu?: boolean;
  showValidateButton?: boolean;
  showConfirmButton?: boolean;
  handleConfirm?: () => void;
  handleCloseMenu?: () => void;
  handleValidate?: (questionId: string) => void;
}

const SectionButtons: React.FC<SectionButtonsProps> = ({
  projectId,
  handleSave,
  values,
  hasGenerated,
  showEditButton = true,
  handleEdit = () => {},
  isEditing = true,
  showSaveButton = true,
  allowGeneration = true,
  allowReprompt = false,
  question,
  timeToProcess = '5-10 minutes',
  showGenerateButton = true,
  isMountedOnMenu = false,
  showValidateButton = false,
  showConfirmButton = false,
  handleConfirm,
  handleCloseMenu,
  handleValidate,
}) => {
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [openRegenerateDialog, setOpenRegenerateDialog] = useState<boolean>(false);
  const analytics = useAnalytics();

  const handleCloseDialog = (sendAnalyticEvent = true) => {
    if (sendAnalyticEvent) {
      analytics.event(
        AnalyticsEvent.WM_IA_A_APPLY_CHANGES_DIALOG_CLOSE,
        {
          id: question?.id,
          value: question?.pageName,
        },
        {
          projectId,
          runId: question?.runId,
          scope: question?.scope,
          questionId: question?.id,
          pageName: question?.pageName,
        }
      );
    }
    setOpenDialog(false);
  };

  const handleOpenDialog = async () => {
    analytics.event(
      AnalyticsEvent.WM_IA_A_APPLY_CHANGES,
      {
        id: question?.id,
        value: question?.pageName,
      },
      {
        projectId,
        runId: question?.runId,
        scope: question?.scope,
        questionId: question?.id,
        pageName: question?.pageName,
      }
    );

    setOpenDialog(true);
  };

  const handleConfirmApplyChanges = async () => {
    analytics.event(
      AnalyticsEvent.WM_IA_A_APPLY_CHANGES_CONFIRM,
      {
        id: question?.id,
        value: question?.pageName,
      },
      {
        projectId,
        runId: question?.runId,
        scope: question?.scope,
        questionId: question?.id,
        pageName: question?.pageName,
      }
    );
    handleCloseDialog(false);
    await handleSave(
      values,
      isEditing ? UpdatedReason.USER_EDIT : undefined,
      ProcessReason.APPLY_CHANGES
    );
    handleEdit(false);
  };

  const regenerate = useCallback(
    (newPrompt?: string) => {
      analytics.event(
        AnalyticsEvent.WM_IA_A_QUESTION_GENERATE,
        {
          id: question?.id,
          value: question?.pageName,
        },
        {
          projectId,
          runId: question?.runId,
          scope: question?.scope,
          questionId: question?.id,
          pageName: question?.pageName,
        }
      );

      let overwriteQuestionData: Partial<ProjectRunAnalysisQuestion> | undefined = undefined;

      if (newPrompt) {
        analytics.event(
          AnalyticsEvent.WM_IA_A_QUESTION_REPROMPT,
          {
            id: question?.id,
            value: newPrompt,
          },
          {
            projectId,
            newPrompt,
            runId: question?.runId,
            scope: question?.scope,
            questionId: question?.id,
            pageName: question?.pageName,
          }
        );

        overwriteQuestionData = {
          prompt: newPrompt,
          enrichedPrompt: null,
        };
      }

      handleSave(
        values,
        UpdatedReason.REPROCESS,
        ProcessReason.PROCESS_THIS,
        overwriteQuestionData
      );
      handleEdit(false);
      if (handleCloseMenu) {
        handleCloseMenu();
      }
    },
    [analytics, question, projectId, handleSave, values, handleEdit, handleCloseMenu]
  );

  const handleGenerate = useCallback(() => {
    if (allowReprompt) {
      setOpenRegenerateDialog(true);
    } else {
      regenerate();
    }
  }, [regenerate, allowReprompt]);

  return (
    <div
      style={{
        display: 'flex',
        gap: '16px',
        width: '100%',
        justifyContent: showGenerateButton || showValidateButton ? 'space-between' : 'flex-end',
      }}
    >
      {showGenerateButton && (
        <Tooltip
          title={
            <div style={{ whiteSpace: 'pre-line' }}>
              {hasGenerated
                ? "Not happy with the LLM output or did you change things on previous pages? Try regenerating this output!\n\nDon't worry, you can go back to this version"
                : 'Generate a response from the LLM'}
            </div>
          }
          placement="left"
          arrow
        >
          <span>
            <Button
              variant={isMountedOnMenu ? 'text' : 'contained'}
              color={isMountedOnMenu ? 'inherit' : 'primary'}
              startIcon={isMountedOnMenu ? <PlayArrowOutlined /> : undefined}
              onClick={handleGenerate}
              disabled={!allowGeneration}
              sx={{
                '&:hover': isMountedOnMenu ? { backgroundColor: 'inherit' } : {},
              }}
            >
              {hasGenerated ? 'Regenerate This' : 'Generate This'}
            </Button>
          </span>
        </Tooltip>
      )}
      {showValidateButton && (
        <div>
          <Tooltip
            title={
              <div style={{ whiteSpace: 'pre-line' }}>Have the LLM validate its own findings</div>
            }
            placement="left"
            arrow
          >
            <Button
              variant={isMountedOnMenu ? 'text' : 'contained'}
              color={isMountedOnMenu ? 'inherit' : 'primary'}
              startIcon={isMountedOnMenu ? <VerifiedOutlined /> : undefined}
              onClick={() => {
                analytics.event(
                  AnalyticsEvent.WM_IA_D_VALIDATE,
                  {
                    id: question?.id,
                    value: question?.pageName,
                  },
                  {
                    projectId,
                    runId: question?.runId,
                    scope: question?.scope,
                    questionId: question?.id,
                    pageName: question?.pageName,
                  }
                );
                if (handleValidate) {
                  handleValidate(question?.id || '');
                }
                if (handleCloseMenu) {
                  handleCloseMenu();
                }
              }}
              disabled={!allowGeneration}
              sx={{
                '&:hover': isMountedOnMenu ? { backgroundColor: 'inherit' } : {},
              }}
            >
              Validate
            </Button>
          </Tooltip>
        </div>
      )}
      <div style={{ display: 'flex', gap: '16px' }}>
        {!isEditing && showConfirmButton && (
          <Tooltip title="Change the status of this question to 'Confirmed'">
            <Button variant="contained" onClick={handleConfirm}>
              Confirm
            </Button>
          </Tooltip>
        )}
        {showEditButton &&
          (isEditing ? (
            <Button
              variant="outlined"
              onClick={() => {
                analytics.event(
                  AnalyticsEvent.WM_IA_A_QUESTION_EDIT,
                  {
                    id: question?.id,
                    value: `Page=${question?.pageName},IsEditing=${true}`,
                  },
                  {
                    projectId,
                    runId: question?.runId,
                    scope: question?.scope,
                    questionId: question?.id,
                    pageName: question?.pageName,
                    wasEditingResponse: false,
                    nowEditingResponse: true,
                  }
                );
                handleEdit(false);
              }}
            >
              Discard Changes
            </Button>
          ) : (
            <Tooltip
              title={
                <div style={{ whiteSpace: 'pre-line' }}>
                  {
                    'Edit this response to tweak what the LLM generated.\n\nYou should then apply the changes to downstream dependencies so the LLM can update its responses with the right information.'
                  }
                </div>
              }
            >
              <span>
                <Button
                  variant="outlined"
                  startIcon={<Edit />}
                  onClick={() => {
                    analytics.event(
                      AnalyticsEvent.WM_IA_A_QUESTION_EDIT,
                      {
                        id: question?.id,
                        value: `Page=${question?.pageName},IsEditing=${false}`,
                      },
                      {
                        projectId,
                        runId: question?.runId,
                        scope: question?.scope,
                        questionId: question?.id,
                        pageName: question?.pageName,
                        wasEditingResponse: true,
                        nowEditingResponse: false,
                      }
                    );
                    handleEdit(true);
                  }}
                  sx={{ height: '100%' }}
                >
                  Edit
                </Button>
              </span>
            </Tooltip>
          ))}
        {showSaveButton && (
          <Tooltip
            title={
              <div style={{ whiteSpace: 'pre-line' }}>
                {
                  "Save what you have. Use this to keep working on this response later.\n\nWhen you're ready, apply the changes to downstream dependencies so the LLM can update its responses with the right information."
                }
              </div>
            }
          >
            <span>
              <Button
                disabled={!isEditing}
                variant="outlined"
                onClick={() => {
                  analytics.event(
                    AnalyticsEvent.WM_IA_A_QUESTION_SAVE,
                    {
                      id: question?.id,
                      value: question?.pageName,
                    },
                    {
                      projectId,
                      runId: question?.runId,
                      scope: question?.scope,
                      questionId: question?.id,
                      pageName: question?.pageName,
                    }
                  );
                  handleSave(values, UpdatedReason.USER_EDIT);
                  handleEdit(false);
                }}
                sx={{ height: '100%' }}
              >
                Save
              </Button>
            </span>
          </Tooltip>
        )}
        {showEditButton && (
          <Tooltip
            title={
              <div style={{ whiteSpace: 'pre-line' }}>
                {isEditing
                  ? 'Have this how you want it? Save these changes and immediately "apply" them to downstream dependencies. This will regenerate LLM responses on later pages that depend on this one.\n\nDon\'t worry! You can switch between versions if you liked what you had, but give it a try!'
                  : "Have this how you want it? Apply it to downstream dependencies. This will regenerate LLM responses on later pages that depend on this one.\n\nDon't worry! You can switch between versions if you liked what you had, but give it a try!"}
              </div>
            }
          >
            <span>
              <Button
                variant={showConfirmButton ? 'outlined' : 'contained'}
                onClick={handleOpenDialog}
              >
                {isEditing ? 'Save and Apply Changes' : 'Apply Changes'}
              </Button>
            </span>
          </Tooltip>
        )}
        <ApplyChangesDialog
          open={openDialog}
          question={question}
          timeToProcess={timeToProcess}
          onConfirm={handleConfirmApplyChanges}
          onClose={() => setOpenDialog(false)}
        />
        {openRegenerateDialog && (
          <RegenerateDialog
            promptType={question?.promptType || ''}
            currentPrompt={question?.prompt || ''}
            handleSave={regenerate}
            handleClose={() => setOpenRegenerateDialog(false)}
          />
        )}
      </div>
    </div>
  );
};

export default SectionButtons;
